import React, { useMemo } from 'react';
import { Box } from '@common-fe/common-fe';

import Permissions from '@/common/permissions';
import { useHasAccess } from '@/modules/core/hooks';
import ModuleWrapper from '@/modules/plan/ModuleWrapper';

import { PLAN_MODULES } from '../../plan.constants';

import ActivatePlan from './ActivatePlan';
import FinalSetUpModal from './FinalSetupModal';
import { useHandlePlanSetUp } from './hooks';
import SavePlan from './SavePlan';
import ValidationNode from './ValidationNode';

interface Props {
  isEditMode?: boolean;
  planName?: string;
  isPlanActive?: boolean;
  isTemplateMode?: boolean;
  isPlanTerminated?: boolean;
  isEditableTemplate?: boolean;
  isTemplateUnderUpdate?: boolean;
  saveButtonRef?: React.RefObject<HTMLButtonElement | null>;
  activateButtonRef?: React.RefObject<HTMLButtonElement | null>;
  reviewButtonRef?: React.RefObject<HTMLButtonElement | null>;
}
const FinalSetup: React.FC<Props> = ({
  isEditMode,
  planName,
  isTemplateMode,
  isPlanActive,
  isPlanTerminated,
  isEditableTemplate,
  isTemplateUnderUpdate,
  saveButtonRef,
  activateButtonRef,
  reviewButtonRef,
}) => {
  const {
    onSaveDraftHandler,
    onUpdateHandler,
    onActivateHandler,

    errorMessages,
    successMessages,
    pendingMessages,

    activateErrorMessages,
    activateSuccessMessages,
    activatePedingMessages,
  } = useHandlePlanSetUp(isTemplateMode);
  const isHasAccess = useHasAccess([
    {
      permission: Permissions.EDIT_DRAFT_PLAN,
    },
    {
      permission: Permissions.ACTIVATE_PLAN,
    },
    {
      permission: Permissions.MODIFY_ACTIVE_PLAN_TEMPLATE,
    },
  ]);

  const activatePlanProps = useMemo(
    () => ({
      activateButtonRef,
      errorMessages: activateErrorMessages,
      successMessages: activateSuccessMessages,
      pendingMessages: activatePedingMessages,
      isTemplateMode,
      viewMode: !isEditMode,
      onActivate: onActivateHandler,
      onUpdate: onUpdateHandler,
      isPlanActive,
      planName,
      isPlanTerminated,
      isEditableTemplate,
      isTemplateUnderUpdate,
    }),
    [
      activateButtonRef,
      activateErrorMessages,
      activateSuccessMessages,
      activatePedingMessages,
      isTemplateMode,
      isEditMode,
      onActivateHandler,
      onUpdateHandler,
      isPlanActive,
      planName,
      isPlanTerminated,
      isEditableTemplate,
      isTemplateUnderUpdate,
    ]
  );
  const isSingleUpdateBlock = isPlanActive || isTemplateUnderUpdate;

  if (!isHasAccess || (isTemplateMode && isPlanActive && !isEditableTemplate)) {
    return null;
  }
  return (
    <ModuleWrapper anchorId={PLAN_MODULES.FINAL_SETUP} title="Final Setup" expandless={false}>
      <ValidationNode isTemplateMode={isTemplateMode} />
      <Box pad="spacing8" round="container1Round" direction="row" align="stretch" flex="grow">
        <FinalSetUpModal
          mode={isTemplateMode ? 'Plan Template' : 'Plan'}
          handleSave={onSaveDraftHandler}
          handleUpdate={onUpdateHandler}
          handleActivate={onActivateHandler}
        />

        {!isSingleUpdateBlock && (
          <SavePlan
            saveButtonRef={saveButtonRef}
            reviewButtonRef={reviewButtonRef}
            errorMessages={errorMessages}
            successMessages={successMessages}
            pendingMessages={pendingMessages}
            isTemplateMode={isTemplateMode}
            viewMode={!isEditMode}
            onSave={onSaveDraftHandler}
            isPlanTerminated={isPlanTerminated}
          />
        )}
        {!isSingleUpdateBlock && <Box pad="spacing8" />}
        {isSingleUpdateBlock ? (
          <Box width="464px">
            <ActivatePlan {...activatePlanProps} />
          </Box>
        ) : (
          <ActivatePlan {...activatePlanProps} />
        )}
      </Box>
    </ModuleWrapper>
  );
};

export default React.memo(FinalSetup);
